import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  BuildingOffice2Icon,
  CurrencyDollarIcon,
  ReceiptPercentIcon,
  CalendarIcon,
  GlobeAltIcon,
  TagIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const CATEGORIES = [
  { value: 'REPAIRS',                label: 'Repairs',              color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/20' },
  { value: 'MAINTENANCE',            label: 'Maintenance',          color: 'text-orange-400',  bg: 'bg-orange-500/10 border-orange-500/20' },
  { value: 'UTILITIES',              label: 'Utilities',            color: 'text-blue-400',    bg: 'bg-blue-500/10 border-blue-500/20' },
  { value: 'SUPPLIES',               label: 'Supplies',             color: 'text-cyan-300',  bg: 'bg-cyan-400/10 border-cyan-400/20' },
  { value: 'PROFESSIONAL_SERVICES',  label: 'Professional Services',color: 'text-indigo-400',  bg: 'bg-indigo-500/10 border-indigo-500/20' },
  { value: 'LANDSCAPING',            label: 'Landscaping',          color: 'text-green-400',   bg: 'bg-green-500/10 border-green-500/20' },
  { value: 'CLEANING',               label: 'Cleaning',             color: 'text-cyan-400',    bg: 'bg-cyan-500/10 border-cyan-500/20' },
  { value: 'INSURANCE_CLAIM',        label: 'Insurance Claim',      color: 'text-pink-400',    bg: 'bg-pink-500/10 border-pink-500/20' },
  { value: 'EMERGENCY',              label: 'Emergency',            color: 'text-red-500',     bg: 'bg-red-500/15 border-red-500/30' },
  { value: 'OTHER',                  label: 'Other',                color: 'text-white/50',    bg: 'bg-white/5 border-white/10' },
];

const catInfo = (c: string) => CATEGORIES.find(x => x.value === c) || CATEGORIES[CATEGORIES.length - 1];

interface Expense {
  id: string;
  description: string;
  category: string;
  amount: number;
  expenseDate: string;
  vendor?: string | null;
  notes?: string | null;
  addressId?: string | null;
  address?: { id: string; name: string; city: string; state: string } | null;
  createdBy?: { id: string; name: string } | null;
}

interface Summary {
  thisMonth: number;
  thisMonthCount: number;
  ytd: number;
  ytdCount: number;
  byCategory: { category: string; total: number }[];
  byProperty: { addressId: string | null; name: string; total: number }[];
  generalThisMonth: number;
}

interface PropertyOption {
  id: string;
  name: string;
  city: string;
  state: string;
}

interface ExpenseForm {
  description: string;
  category: string;
  amount: string;
  expenseDate: string;
  vendor: string;
  notes: string;
  addressId: string; // '' = general
}

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm: ExpenseForm = {
  description: '',
  category: 'REPAIRS',
  amount: '',
  expenseDate: todayISO(),
  vendor: '',
  notes: '',
  addressId: ''
};

const Expenses: React.FC = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [loading, setLoading] = useState(true);

  const [filterAddressId, setFilterAddressId] = useState<string>(''); // '' = all, 'general' = portfolio-wide only
  const [filterMonth, setFilterMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ExpenseForm>(emptyForm);

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(''), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const headers = useCallback(() => {
    const g = localStorage.getItem('selectedPropertyGroup');
    return g ? { 'x-selected-group': g } : {};
  }, []);

  const fetchAll = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterMonth) params.month = filterMonth;
      if (filterAddressId) params.addressId = filterAddressId;

      const [expRes, sumRes, propRes] = await Promise.all([
        api.get<Expense[]>('/api/expenses', { headers: headers(), params }),
        api.get<Summary>('/api/expenses/summary', { headers: headers() }),
        api.get<PropertyOption[]>('/api/addresses', { headers: headers() }),
      ]);
      setExpenses(expRes.data);
      setSummary(sumRes.data);
      setProperties(propRes.data);
    } catch (e) {
      console.error('Error loading expenses:', e);
      setMessage('Failed to load expenses');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }, [user?.token, headers, filterMonth, filterAddressId]);

  useEffect(() => {
    if (user?.role === 'ADMIN') fetchAll();
    else setLoading(false);
  }, [fetchAll, user?.role]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, expenseDate: todayISO() });
    setShowModal(true);
  };

  const openEdit = (e: Expense) => {
    setEditingId(e.id);
    setForm({
      description: e.description,
      category: e.category,
      amount: String(e.amount),
      expenseDate: e.expenseDate.slice(0, 10),
      vendor: e.vendor || '',
      notes: e.notes || '',
      addressId: e.addressId || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.description || !form.category || !form.amount || !form.expenseDate) {
      setMessage('Description, category, amount, and date are required');
      setMessageType('error');
      return;
    }
    if (parseFloat(form.amount) <= 0) {
      setMessage('Amount must be greater than zero');
      setMessageType('error');
      return;
    }
    try {
      const payload = {
        description: form.description,
        category: form.category,
        amount: parseFloat(form.amount),
        expenseDate: form.expenseDate,
        vendor: form.vendor || null,
        notes: form.notes || null,
        addressId: form.addressId || null
      };
      if (editingId) {
        await api.put(`/api/expenses/${editingId}`, payload, { headers: headers() });
        setMessage('Expense updated');
      } else {
        await api.post('/api/expenses', payload, { headers: headers() });
        setMessage('Expense recorded');
      }
      setMessageType('success');
      closeModal();
      await fetchAll();
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Failed to save expense');
      setMessageType('error');
    }
  };

  const handleDelete = async (id: string, description: string) => {
    if (!window.confirm(`Delete expense "${description}"?`)) return;
    try {
      await api.delete(`/api/expenses/${id}`, { headers: headers() });
      setMessage('Expense deleted');
      setMessageType('success');
      await fetchAll();
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Failed to delete');
      setMessageType('error');
    }
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (user?.role !== 'ADMIN') {
    return (
      <div className="text-center py-8">
        <p className="text-white/60">Admin access required.</p>
      </div>
    );
  }

  if (loading && !summary) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#22d3ee] mx-auto"></div>
        <p className="text-white/60 mt-2">Loading expenses…</p>
      </div>
    );
  }

  // Year for month input UI
  const monthLabel = filterMonth
    ? new Date(filterMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'All time';

  return (
    <div className="space-y-8">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Expenses</h1>
          <p className="text-white/50 mt-1">Track repairs, supplies, services, and other property spending</p>
        </div>
        <button onClick={openAdd} className="btn-primary flex items-center gap-2">
          <PlusIcon className="w-5 h-5" />
          Record Expense
        </button>
      </div>

      {/* ── Messages ── */}
      {message && (
        <div className={`p-4 rounded-xl border ${messageType === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
          {message}
        </div>
      )}

      {/* ── Summary Stats ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="floating-card text-center py-5">
          <p className="text-2xl font-bold text-amber-400">{fmt(summary?.thisMonth ?? 0)}</p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-wide">This Month</p>
          <p className="text-xs text-white/30 mt-0.5">{summary?.thisMonthCount ?? 0} expense{(summary?.thisMonthCount ?? 0) !== 1 ? 's' : ''}</p>
        </div>
        <div className="floating-card text-center py-5">
          <p className="text-2xl font-bold text-orange-400">{fmt(summary?.ytd ?? 0)}</p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-wide">Year to Date</p>
          <p className="text-xs text-white/30 mt-0.5">{summary?.ytdCount ?? 0} total expense{(summary?.ytdCount ?? 0) !== 1 ? 's' : ''}</p>
        </div>
        <div className="floating-card text-center py-5">
          <p className="text-2xl font-bold text-cyan-300">{fmt(summary?.generalThisMonth ?? 0)}</p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-wide">General (This Month)</p>
          <p className="text-xs text-white/30 mt-0.5">portfolio-wide costs</p>
        </div>
        <div className="floating-card text-center py-5">
          <p className="text-2xl font-bold text-white">
            {(summary?.thisMonthCount ?? 0) > 0 ? fmt((summary!.thisMonth) / summary!.thisMonthCount) : fmt(0)}
          </p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-wide">Avg / Expense</p>
          <p className="text-xs text-white/30 mt-0.5">this month</p>
        </div>
      </div>

      {/* ── Category Breakdown (this month) ── */}
      {summary && summary.byCategory.length > 0 && (
        <div className="floating-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <TagIcon className="w-4 h-4 text-white/40" />
              Spending by Category — This Month
            </h3>
            <p className="text-xs text-white/40">{monthLabel}</p>
          </div>
          <div className="space-y-2">
            {summary.byCategory.map(c => {
              const ci = catInfo(c.category);
              const pct = summary.thisMonth > 0 ? (c.total / summary.thisMonth) * 100 : 0;
              return (
                <div key={c.category} className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2 py-1 rounded border w-44 flex-shrink-0 ${ci.bg} ${ci.color}`}>
                    {ci.label}
                  </span>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${ci.bg.replace('/10', '/40').replace('border-', 'bg-').split(' ')[0]}`}
                      style={{ width: `${pct}%`, backgroundColor: 'currentColor' }}
                    />
                  </div>
                  <span className={`text-sm font-semibold w-20 text-right flex-shrink-0 ${ci.color}`}>{fmt(c.total)}</span>
                  <span className="text-xs text-white/30 w-12 text-right flex-shrink-0">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="floating-card">
        <div className="flex items-center gap-3 mb-3">
          <FunnelIcon className="w-4 h-4 text-white/40" />
          <p className="text-sm font-medium text-white/60">Filters</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide">Month</label>
            <input
              type="month"
              value={filterMonth}
              onChange={e => setFilterMonth(e.target.value)}
              className="input-field"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide">Property</label>
            <select
              value={filterAddressId}
              onChange={e => setFilterAddressId(e.target.value)}
              className="input-field"
            >
              <option value="">All properties (incl. general)</option>
              <option value="general">General (portfolio-wide) only</option>
              <optgroup label="Specific properties">
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name} — {p.city}, {p.state}</option>
                ))}
              </optgroup>
            </select>
          </div>
          {(filterMonth || filterAddressId) && (
            <button
              onClick={() => { setFilterMonth(''); setFilterAddressId(''); }}
              className="btn-secondary text-sm py-2.5"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Expense List ── */}
      <div className="floating-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-white">Expense Log</h3>
            <p className="text-xs text-white/40 mt-0.5">
              {expenses.length} expense{expenses.length !== 1 ? 's' : ''}
              {filterMonth && ` for ${monthLabel}`}
              {filterAddressId === 'general' && ' (portfolio-wide only)'}
              {filterAddressId && filterAddressId !== 'general' &&
                ` for ${properties.find(p => p.id === filterAddressId)?.name ?? 'selected property'}`}
            </p>
          </div>
          <p className="text-sm font-bold text-amber-400">
            Total: {fmt(expenses.reduce((s, e) => s + Number(e.amount), 0))}
          </p>
        </div>

        {expenses.length === 0 ? (
          <div className="text-center py-12">
            <ReceiptPercentIcon className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 mb-4">No expenses recorded for this period.</p>
            <button onClick={openAdd} className="btn-primary">Record Your First Expense</button>
          </div>
        ) : (
          <div className="space-y-2">
            {expenses.map(e => {
              const ci = catInfo(e.category);
              const isGeneral = !e.addressId;
              return (
                <div key={e.id} className="rounded-xl p-4 bg-white/3 border border-white/8 hover:bg-white/5 transition-colors">
                  <div className="flex items-start gap-3">
                    {/* Date column */}
                    <div className="text-center w-14 flex-shrink-0 pt-1">
                      <p className="text-xs text-white/40 uppercase tracking-wide">
                        {new Date(e.expenseDate).toLocaleDateString('en-US', { month: 'short' })}
                      </p>
                      <p className="text-lg font-bold text-white">
                        {new Date(e.expenseDate).getDate()}
                      </p>
                    </div>

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded border ${ci.bg} ${ci.color}`}>
                          {ci.label}
                        </span>
                        {isGeneral ? (
                          <span className="text-xs flex items-center gap-1 px-2 py-0.5 rounded border bg-cyan-400/10 border-cyan-400/20 text-cyan-300">
                            <GlobeAltIcon className="w-3 h-3" />
                            General · All properties
                          </span>
                        ) : (
                          <span className="text-xs flex items-center gap-1 px-2 py-0.5 rounded border bg-blue-500/10 border-blue-500/20 text-blue-400">
                            <BuildingOffice2Icon className="w-3 h-3" />
                            {e.address?.name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-white">{e.description}</p>
                      {e.vendor && <p className="text-xs text-white/50 mt-0.5">Paid to: {e.vendor}</p>}
                      {e.notes && <p className="text-xs text-white/30 italic mt-0.5">{e.notes}</p>}
                    </div>

                    {/* Amount */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-base font-bold text-amber-400">{fmt(Number(e.amount))}</p>
                      <p className="text-xs text-white/40">{fmtDate(e.expenseDate)}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => openEdit(e)}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                      >
                        <PencilIcon className="w-4 h-4 text-white/40 hover:text-white" />
                      </button>
                      <button
                        onClick={() => handleDelete(e.id, e.description)}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors"
                      >
                        <TrashIcon className="w-4 h-4 text-white/40 hover:text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ══════════════════ EXPENSE MODAL ══════════════════ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#1a1a1b] border border-white/10 rounded-2xl w-full max-w-xl my-8">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold text-white">{editingId ? 'Edit Expense' : 'Record New Expense'}</h2>
                <p className="text-sm text-white/50 mt-0.5">Log a one-time expense for a property or all properties</p>
              </div>
              <button onClick={closeModal} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <XMarkIcon className="w-5 h-5 text-white/60" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">Description *</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="input-field"
                  placeholder="e.g. Plumbing repair, Lawn care, New door lock"
                />
              </div>

              {/* Category + Amount */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1.5">Category *</label>
                  <select
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="input-field"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1.5">Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.amount}
                      onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                      className="input-field pl-7"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Property */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">Apply To *</label>
                <select
                  value={form.addressId}
                  onChange={e => setForm(f => ({ ...f, addressId: e.target.value }))}
                  className="input-field"
                >
                  <option value="">🌐 General — All properties (portfolio-wide expense)</option>
                  <optgroup label="Specific property">
                    {properties.map(p => (
                      <option key={p.id} value={p.id}>{p.name} — {p.city}, {p.state}</option>
                    ))}
                  </optgroup>
                </select>
                <p className="text-xs text-white/30 mt-1">
                  {form.addressId
                    ? 'This expense will count against the selected property in cash flow.'
                    : 'A general expense applies portfolio-wide and shows in the "General" total on the cash flow page.'}
                </p>
              </div>

              {/* Date + Vendor */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1.5 flex items-center gap-1.5">
                    <CalendarIcon className="w-4 h-4 text-white/40" />
                    Expense Date *
                  </label>
                  <input
                    type="date"
                    value={form.expenseDate}
                    onChange={e => setForm(f => ({ ...f, expenseDate: e.target.value }))}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1.5">Vendor / Paid To</label>
                  <input
                    type="text"
                    value={form.vendor}
                    onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                    className="input-field"
                    placeholder="e.g. Acme Plumbing"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="input-field"
                  rows={2}
                  placeholder="Optional details..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button onClick={closeModal} className="btn-secondary flex-1">Cancel</button>
                <button onClick={handleSave} className="btn-primary flex-1 flex items-center justify-center gap-2">
                  <CurrencyDollarIcon className="w-4 h-4" />
                  {editingId ? 'Save Changes' : 'Record Expense'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
